/**
 * formUtils.ts – LP共通 ID生成 / パラメータ管理 / ペイロード構築
 *
 * 呼び出し順序:
 *   1. ページ表示時 → initPageTracking()  ← visitor_id + UTM/affilを確定・保存
 *   2. フォーム送信時 → buildPayload()  ← conversion_id生成 + sessionStorageから読取
 *
 * LP固有設定: FORM_FIELDS の配列のみ変更する。
 */

// ── キー定数 ─────────────────────────────────────────
/** 全LPで共通: localStorage に永続化する visitor_id キー */
const VISITOR_ID_KEY = 'lp_common_visitor_id';
/** セッション中のマーケティングパラメータ保存キー（sessionStorage） */
const SESSION_PARAM_KEY = 'lp_session_params';

// ── 全10フィールド（DBカラムと一致） ─────────────────
const ALL_PERSONAL_FIELDS = [
  'qualification',
  'birth_year',
  'birth_month',
  'birth_day',
  'name',
  'phone',
  'postal_code',
  'prefecture',
  'work_style',
  'transfer_timing',
] as const;

export type PersonalFieldName = (typeof ALL_PERSONAL_FIELDS)[number];

/**
 * このLPで取得するフォーム項目。
 * 配列に含まれない項目は自動的に "" で送信される。
 *
 * LP3（標準）: ["qualification","name","phone","postal_code","prefecture","transfer_timing"]
 * LP4（フル） : 全10項目
 */
export const FORM_FIELDS: PersonalFieldName[] = [
  'qualification',
  'transfer_timing',
  'name',
  'postal_code',
  'phone',
];

// ── UUID v4 ──────────────────────────────────────────
function uuidv4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ── visitor_id ───────────────────────────────────────
/**
 * visitor_id を取得または新規生成する。
 * localStorage に永続化し、同一ドメインの全LPで共通識別する。
 * 形式: vis_{UUID v4}
 * ページ表示時（DOMContentLoaded相当）に initPageTracking() 経由で呼ぶ。
 */
export function getOrCreateVisitorId(): string {
  try {
    const existing = localStorage.getItem(VISITOR_ID_KEY);
    if (existing) return existing;
    const id = `vis_${uuidv4()}`;
    localStorage.setItem(VISITOR_ID_KEY, id);
    return id;
  } catch {
    return `vis_${uuidv4()}`; // プライベートブラウザ等のフォールバック
  }
}

// ── sessionStorage: UTM / アフィリエイト保存 ─────────
interface SessionParams {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
  aff_company: string;
  aff_id: string;
  click_id: string;
  referrer: string;
  landing_page: string;
}

/**
 * ページ表示時に URLパラメータ・referrer を sessionStorage に保存する。
 * ユーザーがページ内でURLが変わっても初回の値を保持する。
 */
export function initSessionParams(): void {
  try {
    const p = new URLSearchParams(window.location.search);
    const params: SessionParams = {
      utm_source:   p.get('utm_source')   ?? '',
      utm_medium:   p.get('utm_medium')   ?? '',
      utm_campaign: p.get('utm_campaign') ?? '',
      utm_term:     p.get('utm_term')     ?? '',
      utm_content:  p.get('utm_content')  ?? '',
      aff_company:  p.get('aff_company')  ?? '',
      aff_id:       p.get('aff_id')       ?? '',
      click_id:     p.get('click_id')     ?? '',
      referrer:     typeof document !== 'undefined' ? (document.referrer ?? '') : '',
      landing_page: getLpId(),
    };
    sessionStorage.setItem(SESSION_PARAM_KEY, JSON.stringify(params));
  } catch {
    // sessionStorage 利用不可時はスキップ
  }
}

/** sessionStorage からマーケティングパラメータを取得する（フォーム送信時に呼ぶ）。 */
function getSessionParams(): SessionParams {
  const empty: SessionParams = {
    utm_source: '', utm_medium: '', utm_campaign: '', utm_term: '', utm_content: '',
    aff_company: '', aff_id: '', click_id: '', referrer: '', landing_page: '',
  };
  try {
    const raw = sessionStorage.getItem(SESSION_PARAM_KEY);
    if (!raw) return empty;
    return { ...empty, ...JSON.parse(raw) };
  } catch {
    return empty;
  }
}

/**
 * ページ表示時に呼び出す初期化処理をまとめたヘルパー。
 * visitor_id 生成 + セッションパラメータ保存を一括実行する。
 */
export function initPageTracking(): void {
  getOrCreateVisitorId(); // localStorage に保存（副作用）
  initSessionParams();    // sessionStorage に保存（副作用）
}

// ── LP ID ─────────────────────────────────────────────
/**
 * lp_id を URL から自動取得する。
 * クエリパラメータを除去し、末尾スラッシュを統一する。
 * 例: "https://lp.nijikango.co.jp/"
 */
export function getLpId(): string {
  const base = window.location.origin + window.location.pathname;
  return base.endsWith('/') ? base : `${base}/`;
}

// ── conversion_id ────────────────────────────────────
/**
 * conversion_id をフォーム送信ごとに新規生成する（localStorage には保存しない）。
 * 形式: cvid_{YYYYMMDD}_{ランダム英数字8桁}
 */
export function generateConversionId(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm   = String(now.getMonth() + 1).padStart(2, '0');
  const dd   = String(now.getDate()).padStart(2, '0');
  const dateStr = `${yyyy}${mm}${dd}`;
  const CHARS   = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const random  = Array.from({ length: 8 }, () => CHARS[Math.floor(Math.random() * CHARS.length)]).join('');
  return `cvid_${dateStr}_${random}`;
}

// ── ペイロード構築 ────────────────────────────────────
/**
 * DB送信用の完全なペイロードを構築する。
 * フォーム送信時に呼び出す（conversion_id がここで生成される）。
 *
 * @param formData - フォームで収集した個人情報。未収集項目は自動で "" になる。
 */
export function buildPayload(
  formData: Partial<Record<PersonalFieldName, string>>
): Record<string, string> {
  // 全10フィールド: 収集した値 or 空文字
  const personalInfo: Record<string, string> = {};
  for (const field of ALL_PERSONAL_FIELDS) {
    personalInfo[field] = formData[field] ?? '';
  }

  // sessionStorageからマーケティングパラメータを取得（ページ表示時の値を使用）
  const session = getSessionParams();
  const lpId    = getLpId();

  return {
    // === ID系（自動生成・自動取得）===
    visitor_id:     getOrCreateVisitorId(), // localStorage から取得
    conversion_id:  generateConversionId(), // 送信時に毎回新規生成
    lp_id:          lpId,

    // === 個人情報（フォーム入力値）===
    ...personalInfo,

    // === マーケティング情報（sessionStorageから取得）===
    utm_source:   session.utm_source,
    utm_medium:   session.utm_medium,
    utm_campaign: session.utm_campaign,
    utm_term:     session.utm_term,
    utm_content:  session.utm_content,
    aff_company:  session.aff_company,
    aff_id:       session.aff_id,
    click_id:     session.click_id,
    referrer:     session.referrer,
    landing_page: session.landing_page || lpId,

    // === メタ情報（自動取得）===
    submitted_at: new Date().toISOString(),
    user_agent:   navigator.userAgent ?? '',
  };
}
